"use client";

import { useSession } from "next-auth/react";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { useEffect } from "react";
import { sanitizeEvent } from "./sanitize-event";

function isEmbedded() {
  return typeof window.getEmbedNamespace === "function" && typeof window.getEmbedNamespace() === "string";
}

// Only the professional's internal id and username are sent; patients stay anonymous.
function IdentifyUser() {
  const { data, status } = useSession();
  const userId = data?.user?.id;
  const username = data?.user?.username;
  const isImpersonating = Boolean(data?.user?.impersonatedBy);

  useEffect(() => {
    if (!posthog.__loaded) return;
    // Support staff acting as a professional would be counted as that professional's usage.
    if (isImpersonating) {
      posthog.opt_out_capturing();
      return;
    }
    if (posthog.has_opted_out_capturing()) posthog.opt_in_capturing();
    if (userId) {
      posthog.identify(String(userId), { username });
      return;
    }
    if (status === "unauthenticated" && posthog.get_property("$user_state") === "identified") {
      posthog.reset();
    }
  }, [userId, username, status, isImpersonating]);

  return null;
}

function initPostHog() {
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY || posthog.__loaded) return;
  // Booking pages embedded in the professionals' own websites are out of scope.
  if (isEmbedded()) return;

  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://eu.i.posthog.com",
    // Memory persistence starts a new session on every reload, which cuts recordings into pieces.
    persistence: "localStorage+cookie",
    autocapture: true,
    enable_heatmaps: true,
    person_profiles: "identified_only",
    request_batching: true,
    // PostHogPageView sends it, because the automatic one misses App Router client navigations.
    capture_pageview: false,
    capture_pageleave: true,
    disable_session_recording: false,
    // Booking pages and the dashboard show patient names, phones and notes.
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*",
    },
    // advanced_disable_flags would also turn off remote config, which session replay needs to start.
    advanced_disable_feature_flags: true,
    before_send: sanitizeEvent,
    loaded: (posthog) => {
      if (process.env.NODE_ENV === "development") posthog.debug();
    },
  });
}

// Initialized at module load rather than in an effect: child effects (pageview, identify) run
// before the parent's, so an effect-based init would drop the first pageview.
if (typeof window !== "undefined") initPostHog();

function Provider({ children }: { children: React.ReactNode }) {
  return (
    <PostHogProvider client={posthog}>
      <IdentifyUser />
      {children}
    </PostHogProvider>
  );
}

export default Provider;
